import { MANUAL_PRESENTS_AS } from './presenceMeta';
import type {
  ActivityFlags, ActivityState, PresenceAutomation, PresenceSnapshot, PresenceWire,
} from './types';

/**
 * WHAT THE APP IS DOING, as opposed to what the person is.
 *
 * The SQL resolver takes this as its own input, `p_activity`, separate from
 * `p_auto`, and `cyphex.user_presence.activity_state` is where the server keeps
 * it — derived by trigger from `call_participants` / `meeting_participants`
 * (infra/sql/062, "Never client-set").
 *
 * THIS SET IS NOT A LOOKUP INTO `autoState`, AND THAT IS THE WHOLE BUG.
 * `resolveEffectiveState` used to ask `APP_ACTIVITY.has(snapshot.autoState)`,
 * but the server keeps the two columns SEPARATE: during a real call it sends
 * `auto_state = away` and `activity_state = in_a_call`, so the test found
 * nothing, the activity tier was skipped entirely, and the next branch —
 * `appear_available` — returned `available`. A green tick, mid-call, on the
 * user's own chip and their own tray icon, while every colleague correctly saw
 * In a call.
 *
 * `screenSharing` is here and not in the DB enum: it is a client-only
 * refinement of `presenting` that `toWire` collapses on the way out.
 */
export const APP_ACTIVITY: ReadonlySet<ActivityState> = new Set<ActivityState>([
  'screenSharing',
  'presenting',
  'inAMeeting',
  'inACall',
]);

/**
 * Precedence WITHIN the activity tier — 062's own order, least-available first:
 * Presenting outranks In a call, which outranks In a meeting. Somebody sharing
 * their screen inside a meeting is Presenting, because the more interrupt-averse
 * fact is the true one.
 *
 * `cyphex.presence_activity_of()` ranks a live CALL above a live MEETING, and
 * this table now says the same. It used to disagree — In a meeting sat above In
 * a call in `computeAutoState` — so a client optimistically in both flipped
 * label the moment the server's trigger caught up.
 */
const ACTIVITY_RANK: Readonly<Partial<Record<ActivityState, number>>> = {
  screenSharing: 4,
  presenting: 3,
  inACall: 2,
  inAMeeting: 1,
};

/**
 * The stronger of two activities, either of which may be absent.
 *
 * Used to fold what THIS document knows it is doing (a voice channel, a meeting
 * stage, a call window in the desktop shell) into what the SERVER has derived.
 * The two are not rivals: the local one is an optimistic pre-echo covering the
 * window between joining and the participant row landing, and the server's is
 * the fact every colleague is being shown. `bool_or` inside
 * `presence_activity_of` combines its own two sources in exactly this way.
 */
export function pickActivity(
  a: ActivityState | null,
  b: ActivityState | null,
): ActivityState | null {
  if (a === null) return b;
  if (b === null) return a;
  return (ACTIVITY_RANK[a] ?? 0) >= (ACTIVITY_RANK[b] ?? 0) ? a : b;
}

/**
 * Client mirror of `cyphex.presence_resolve()` — CURRENT as of infra/sql/068,
 * branch for branch, in the same order, with the same answers.
 *
 *   1. appear_offline override (sticky)     2. out-of-office window
 *   3. an unexpired manual SUPPRESSION      4. offline when no connections AND
 *                                              no app activity   (065/067)
 *   5. what the app is doing                6. appear_available  (067)
 *   7. offline when no connections          8. the automatic state
 *
 * THE INVARIANT THIS FILE EXISTS TO KEEP. For any snapshot whose inputs came
 * from the server, this function returns exactly the `effective_state` the
 * server computed from the same inputs. The server and the client therefore
 * cannot disagree about a RULE; the only thing they may differ on is an INPUT
 * this tab owns and the server cannot see — the idle clock behind `autoState`,
 * and the socket behind `connectionCount`. `test/presence.test.mjs` asserts the
 * equality across a fixture generated from the live `cyphex.presence_resolve()`
 * itself, so drift is a failing test rather than a green tick on somebody who is
 * talking to you.
 *
 * Three of the branches are ordered the way they are for reasons written out in
 * the migrations, and none of them may be "tidied":
 *
 *   - 067: `appear_available` is not a suppression. It is the most available
 *     state there is and cannot hide anything. Its header records two named
 *     users showing a green dot to the whole workspace WHILE ON A CALL WITH EACH
 *     OTHER. It therefore sits BELOW the activity tier, and the other manual
 *     states — which the user picked precisely because they are LESS available
 *     than the truth — stay above it.
 *   - 073: and it does NOT mean "do not show me as idle", which is what 067 made
 *     it. Microsoft Teams shows a person who chose Available as Away the moment
 *     they go idle or lock the machine (Graph calls it `AvailableIdle`); here it
 *     pinned them green for a week - through an hour at an empty desk, a locked
 *     screen, everything - because the tray's "Available" and the menu's first
 *     row both set it. It now beats only what the calendar says, and yields to
 *     the auto state whenever that is Away or Offline.
 *   - 065: `connection_count` never counts up (008 forbids per-connection writes
 *     to Postgres; tier 1 owns liveness), so once it hits zero it is zero for
 *     ever, and a gate above the activity resolved a live call to `offline`. The
 *     owner's own row read `connection_count = 0` mid-call while this was being
 *     fixed. Branch 4 is the only place the counter may speak before the
 *     activity, and only when there is no activity to speak about.
 *   - 068: `in_a_call` / `in_a_meeting` / `presenting` are returned VERBATIM.
 *     `dndDuringCalls` / `dndDuringMeetings` do not appear in this function at
 *     all, and must not: both default to true, so relabelling on them made "In a
 *     call" unreachable and told the workspace "Do not disturb" instead — which
 *     answers none of the questions "In a call" answers.
 */
export function resolveEffectiveState(snapshot: PresenceSnapshot, now: number): ActivityState {
  /* `cyphex.presence_manual_live(manual, expires, at)`: the override, or NULL
     once it has lapsed. Computed once and reused, exactly as the SQL does. */
  const manual =
    snapshot.manualState !== null &&
    (snapshot.manualExpiresAt === null || snapshot.manualExpiresAt > now)
      ? snapshot.manualState
      : null;

  /* 1. */
  if (manual === 'appearOffline') return 'offline';

  /* 2. `cyphex.presence_ooo_live(enabled, starts, ends, at)`. */
  const ooo = snapshot.ooo;
  if (
    ooo?.enabled &&
    (ooo.startsAt === null || ooo.startsAt <= now) &&
    (ooo.endsAt === null || ooo.endsAt > now)
  ) {
    return 'outOfOffice';
  }

  /* 3. SUPPRESSIONS ONLY. `appearAvailable` is deliberately not here — see 067
        in the header; `appearOffline` already returned above. */
  if (manual !== null && manual !== 'appearAvailable') return MANUAL_PRESENTS_AS[manual];

  /*
   * THE MIDDLE INPUT, read from where the server actually puts it.
   *
   * Never from `autoState`: the server sends `auto_state = away` beside
   * `activity_state = in_a_call` for a person who is talking right now.
   */
  const activity = snapshot.activityState;

  /* 4. */
  if (snapshot.connectionCount <= 0 && activity === null) return 'offline';

  /* 5. */
  if (activity !== null && APP_ACTIVITY.has(activity)) return activity;

  /* 6. "Available", chosen: beats a calendar Busy, never idle or a lock (073). */
  if (manual === 'appearAvailable' && snapshot.autoState !== 'away' && snapshot.autoState !== 'offline') {
    return 'available';
  }

  /* 7. Reached only by an activity outside the app-state set: branch 4 saw a
        non-null activity and branch 5 did not recognise it. The SQL keeps the
        same trailing gate, for the same reason. */
  if (snapshot.connectionCount <= 0) return 'offline';

  /* 8. */
  return snapshot.autoState;
}

/**
 * The client's `cyphex.presence_activity_of()`: what THIS document, and the
 * desktop shell's other windows, are doing. `null` — not `available` — when
 * there is nothing to report, because `available` is a CONCLUSION and it belongs
 * to the resolver.
 *
 * It is separate from `computeAutoState` because the server keeps `p_activity`
 * and `p_auto` separate, and folding them into one value here is what made the
 * activity invisible to the resolver in the first place. Its output goes to
 * `PresenceSnapshot.activityState` alongside whatever the server has derived,
 * and the two are combined by `pickActivity`.
 */
export function computeActivity(flags: ActivityFlags): ActivityState | null {
  if (flags.screenSharing) return 'screenSharing';
  if (flags.presenting) return 'presenting';
  /* Call above meeting — `presence_activity_of`'s order, not the reverse. */
  if (flags.inCall) return 'inACall';
  if (flags.inMeeting) return 'inAMeeting';
  return null;
}

/**
 * `user_presence.auto_state`: what the PERSON is, from the idle clock and the
 * calendar. Only ever `busy`, `away` or `available` — the app states left this
 * function when `computeActivity` took them, which is the split the server has
 * always had.
 *
 * SAY WHAT THEY ARE DOING. `dndDuringMeetings` / `dndDuringCalls` used to
 * rewrite the app states into Do-not-disturb here, and BOTH DEFAULT TO TRUE — so
 * the normal case was that somebody on a call read "Do not disturb" to the whole
 * workspace, and "In a call" was effectively unreachable. It protected nobody:
 * `in_a_call`, `in_a_meeting` and `presenting` are already `reachable: false` in
 * presenceMeta and already in the ACD's UNAVAILABLE set in
 * services/pstn/queues.ts, neither of which consults the preference. All the
 * relabel achieved was discarding the more useful fact. The preferences are kept
 * as the right home for a NOTIFICATION rule — silence banners while I am on a
 * call — which is what their names promise. What they must not do is decide what
 * other people see. Mirrors infra/sql/068.
 */
export function computeAutoState(
  flags: ActivityFlags,
  automation: PresenceAutomation,
  idleMs: number,
  now: number,
  lastActivityAt: number,
): ActivityState {
  if (flags.calendarBusy) return 'busy';

  const idleFor = idleMs > 0 ? idleMs : now - lastActivityAt;
  if (idleFor >= automation.autoAwayAfterMinutes * 60_000) return 'away';
  return 'available';
}

const TO_WIRE: Record<ActivityState, PresenceWire> = {
  available: 'available',
  busy: 'busy',
  inAMeeting: 'in_a_meeting',
  inACall: 'in_a_call',
  presenting: 'presenting',
  /** The DB enum has no `screen_sharing`; it collapses into `presenting`. */
  screenSharing: 'presenting',
  away: 'away',
  beRightBack: 'be_right_back',
  doNotDisturb: 'do_not_disturb',
  outOfOffice: 'out_of_office',
  offline: 'offline',
};

const FROM_WIRE: Record<PresenceWire, ActivityState> = {
  available: 'available', busy: 'busy', in_a_meeting: 'inAMeeting', in_a_call: 'inACall',
  presenting: 'presenting', away: 'away', be_right_back: 'beRightBack',
  do_not_disturb: 'doNotDisturb', out_of_office: 'outOfOffice', offline: 'offline',
  appear_available: 'available', appear_busy: 'busy', appear_dnd: 'doNotDisturb',
  appear_away: 'away', appear_offline: 'offline',
};

export const toWire = (state: ActivityState): PresenceWire => TO_WIRE[state];
export const fromWire = (wire: PresenceWire): ActivityState => FROM_WIRE[wire];

/**
 * `activity_state` off the wire. `null` stays `null` — it is the normal case and
 * it means "nothing worth reporting", never "available".
 *
 * ONLY AN UNMAPPABLE VALUE BECOMES NULL, and the difference is the last place
 * the mirror diverged from `cyphex.presence_resolve()`. This used to null every
 * member OUTSIDE the app-state set as well, which quietly moved a row from one
 * branch to another: SQL tests `p_activity IS NULL` at branch 4 and
 * `p_activity IN (presenting, in_a_call, in_a_meeting)` at branch 5, so an
 * activity outside that set skips BOTH and lands on the trailing connection gate
 * at branch 7. Nulling it here made branch 4 fire instead, and branch 7 became
 * unreachable — 360 disagreements over a 28 500-row cross-check against the live
 * function, all of the shape `appear_available + no connections + an out-of-set
 * activity`, where SQL says `available` and the client said `offline`.
 *
 * Unreachable in production (`cyphex.presence_activity_of()` emits three values
 * or NULL, and `sameStory()` narrows the wire further), which is precisely why
 * it survived every test: the fixture could not produce it. It is fixed rather
 * than documented because "the client mirrors the server for ALL inputs" is the
 * property the whole file is defended by, and a mirror with a hole in it is not
 * one.
 */
export function activityFromWire(wire: PresenceWire | null | undefined): ActivityState | null {
  if (wire === null || wire === undefined) return null;
  return FROM_WIRE[wire] ?? null;
}
